import { HotspotKey } from '@spreadgroup/common-partner-lib/states';
import store from 'commons/store/Store';
import localeService from 'commons/localeService/localeService';

export type PublishingCategoryConfig = {
  id: string;
  name: string;
  previewImage: string;
};

export type {
  PublishingCategoriesTree,
  CompositionSpecificCategoriesTree,
  PublishingCategoryProductType,
  CompositionSpecificCategoryProductType,
  CategoriesTree,
  CategoryProductType,
};

interface PublishingCategoriesTree {
  id: string;
  names: PublishingCategoryName;
  children: PublishingCategoriesTree[];
  productTypes: PublishingCategoryProductType[];
}

interface CompositionSpecificCategoriesTree {
  id: string;
  names: PublishingCategoryName;
  children: CompositionSpecificCategoriesTree[];
  productTypes: CompositionSpecificCategoryProductType[];
}

interface PublishingCategoryName {
  [locale: string]: string;
}

interface PublishingCategoryProductType {
  id: number;
  hotspots: HotspotKey[];
  appearanceGroups: string[];
  appearanceIds: number[];
}

type CompositionSpecificCategoryProductType = PublishingCategoryProductType & {
  brandName: string;
  defaultAppearance: number;
  defaultViewId: number;
  appearanceIds: number[];
  defaultHotSpot: HotspotKey;
  labels: { id: string }[];
  sizes: number[];
  sizeGroups: string[];
};

type CategoriesTree =
  | PublishingCategoriesTree
  | CompositionSpecificCategoriesTree;
type CategoryProductType =
  | PublishingCategoryProductType
  | CompositionSpecificCategoryProductType;

export type AppearanceGroup = {
  id: string;
  appearanceIds: number[];
  uiColor: string;
};

export type TranslatedAppearanceGroup = AppearanceGroup & { name: string };

export default {
  getAllAvailableHotspots(productTypes?: CategoryProductType[]): HotspotKey[] {
    return productTypes
      ? [
          ...new Set(
            productTypes.reduce(
              (prev, current) => [...prev, ...current.hotspots],
              [] as HotspotKey[],
            ),
          ),
        ]
      : [];
  },
  getAllAvailableAppearanceGroups(
    productTypes?: CategoryProductType[],
  ): TranslatedAppearanceGroup[] {
    if (!productTypes) return [];

    const appearanceGroups = [
      ...new Set(
        productTypes.reduce(
          (prev, current) => [...prev, ...current.appearanceGroups],
          [] as CategoryProductType['appearanceGroups'],
        ),
      ),
    ];
    const groups = store.state.publishingCore.appearanceGroups;
    const appearanceGroupTranslations =
      store.state.publishingCore.translations.appearanceGroups || {};

    return appearanceGroups.map((appearanceGroupId) => ({
      ...groups.find(
        (group: AppearanceGroup) => group.id === appearanceGroupId,
      ),
      name: appearanceGroupTranslations[appearanceGroupId]?.name,
    }));
  },
  getAllAvailableBrandNames(
    productTypes: CompositionSpecificCategoryProductType[],
  ): CompositionSpecificCategoryProductType['brandName'][] {
    return [...new Set(productTypes.map(({ brandName }) => brandName))];
  },
  getAllAvailableSizeGroups(
    productTypes: CompositionSpecificCategoryProductType[],
  ): CompositionSpecificCategoryProductType['sizeGroups'] {
    return [
      ...new Set<string>(
        productTypes.flatMap(({ sizeGroups }) => [...sizeGroups]),
      ),
    ];
  },
  getCategoryName(category: Pick<CategoriesTree, 'id' | 'names'>): string {
    return category.names[localeService.getLocale()] || category.id;
  },
  getAllCategoryIdsOfProductType(
    category: CategoriesTree,
    productTypeId: number,
  ): string[] {
    const findPathToProductType = (
      currentCategory: CategoriesTree,
      targetProductTypeId: number,
      path: string[] = [],
    ): string[] | null => {
      const currentPath = [...path, currentCategory.id];

      const hasProductType = currentCategory.productTypes.some(
        (productType) => productType.id === targetProductTypeId,
      );

      if (hasProductType) {
        return currentPath;
      }

      for (const child of currentCategory.children || []) {
        const foundPath = findPathToProductType(
          child,
          targetProductTypeId,
          currentPath,
        );
        if (foundPath) {
          return foundPath;
        }
      }

      return null;
    };

    const path = findPathToProductType(category, productTypeId);
    return path ? path.reverse() : [];
  },
};
